/**
 * @typedef {import('@/types/structured-data-editor').SdeColumnDef} SdeColumnDef
 * @typedef {import('@/types/structured-data-editor').SdeRow} SdeRow
 */

import {
    rowHasLowConfidenceField,
    rowHasManualField,
} from "@/composables/useConfidenceDisplay";

/** @typedef {'all'|'review'|'excluded'|'modified'|'pending'|'errors'|'manual'} SdeFilterChip */

/**
 * @param {string|undefined} type
 * @returns {SdeColumnDef['fieldType']}
 */
function mapProductTypeFieldType(type) {
    if (type === "integer" || type === "float" || type === "number") {
        return "number";
    }
    if (type === "boolean") {
        return "boolean";
    }
    if (type === "choices") {
        return "choices";
    }
    return "string";
}

/**
 * @param {unknown} choices
 * @returns {string[]}
 */
export function normalizeFieldChoices(choices) {
    if (!Array.isArray(choices)) {
        return [];
    }
    return choices
        .map((choice) => {
            if (typeof choice === "string") {
                return choice;
            }
            if (choice && typeof choice === "object") {
                return choice.text ?? choice.value ?? choice.label ?? null;
            }
            return choice == null ? null : String(choice);
        })
        .filter((choice) => choice != null && String(choice).trim() !== "");
}

/**
 * @param {{ label?: string, fieldType?: SdeColumnDef['fieldType'], choices?: string[], unit?: string }|undefined} existing
 * @param {{ label?: string, type?: string, choices?: unknown[], unit?: string|null }} field
 * @returns {{ label: string, fieldType: SdeColumnDef['fieldType'], choices?: string[], unit?: string }}
 */
function mergeMetadataFieldInfo(existing, field) {
    const incoming = {
        label: field.label || field.code,
        fieldType: mapProductTypeFieldType(field.type),
        choices: normalizeFieldChoices(field.choices),
        unit: field.unit || undefined,
    };
    if (!existing) {
        return incoming;
    }
    const merged = {
        ...existing,
        label: existing.label || incoming.label,
        unit: existing.unit || incoming.unit,
    };
    if (incoming.fieldType === "choices" && incoming.choices.length) {
        merged.fieldType = "choices";
        merged.choices = [...new Set([...(existing.choices || []), ...incoming.choices])];
        return merged;
    }
    if (existing.fieldType === "choices" && existing.choices?.length) {
        return merged;
    }
    if (incoming.fieldType !== "string") {
        merged.fieldType = incoming.fieldType;
    }
    if (!merged.choices?.length && incoming.choices.length) {
        merged.choices = incoming.choices;
    }
    return merged;
}

/**
 * @param {object} producttype
 * @returns {object[]}
 */
function getProductTypeMetadataFields(producttype) {
    return producttype?.metadata_fields || producttype?.fields || [];
}

/**
 * @param {object[]|null|undefined} productTypes
 * @returns {Record<string, { label: string, fieldType: SdeColumnDef['fieldType'], choices?: string[], unit?: string }>}
 */
export function buildMetadataFieldInfoMap(productTypes = []) {
    /** @type {Record<string, { label: string, fieldType: SdeColumnDef['fieldType'], choices?: string[], unit?: string }>} */
    const map = {};
    (productTypes || []).forEach((producttype) => {
        getProductTypeMetadataFields(producttype).forEach((field) => {
            if (!field?.code) {
                return;
            }
            map[field.code] = mergeMetadataFieldInfo(map[field.code], field);
        });
    });
    return map;
}

/**
 * @param {SdeRow} row
 * @param {object[]|null|undefined} productTypes
 * @returns {object|null}
 */
export function resolveRowProductType(row, productTypes = []) {
    const rawValue = row?.fields?.producttype?.value;
    if (rawValue == null || rawValue === "") {
        return null;
    }
    const normalized = String(rawValue).trim().toLowerCase();
    return (
        (productTypes || []).find((producttype) => {
            const code = String(producttype.code || "").trim().toLowerCase();
            const label = String(producttype.label || "").trim().toLowerCase();
            return normalized === code || normalized === label;
        }) || null
    );
}

/**
 * @param {SdeRow} row
 * @param {string} metadataKey
 * @param {object[]|null|undefined} productTypes
 * @param {Record<string, { label?: string, fieldType?: SdeColumnDef['fieldType'], choices?: string[], unit?: string }>} [fieldInfoMap]
 * @returns {{ label: string, fieldType: SdeColumnDef['fieldType'], choices: string[], unit?: string }}
 */
export function resolveMetadataFieldDef(row, metadataKey, productTypes = [], fieldInfoMap = {}) {
    const code = String(metadataKey).replace(/^metadata\./, "");
    const producttype = resolveRowProductType(row, productTypes);
    if (producttype) {
        const field = getProductTypeMetadataFields(producttype).find((item) => item.code === code);
        if (field) {
            const choices = normalizeFieldChoices(field.choices);
            return {
                label: field.label || code,
                fieldType: mapProductTypeFieldType(field.type),
                choices,
                unit: field.unit || undefined,
            };
        }
    }
    const fallback = fieldInfoMap[code];
    return {
        label: fallback?.label || code,
        fieldType: fallback?.fieldType || "string",
        choices: normalizeFieldChoices(fallback?.choices),
        unit: fallback?.unit,
    };
}

/**
 * @param {SdeRow} row
 * @param {SdeColumnDef} column
 * @param {object[]|null|undefined} productTypes
 * @param {Record<string, { label?: string, fieldType?: SdeColumnDef['fieldType'], choices?: string[], unit?: string }>} [fieldInfoMap]
 * @returns {SdeColumnDef['fieldType']}
 */
export function resolveCellFieldType(row, column, productTypes = [], fieldInfoMap = {}) {
    if (column.kind === "metadata" || String(column.key).startsWith("metadata.")) {
        return resolveMetadataFieldDef(row, column.key, productTypes, fieldInfoMap).fieldType;
    }
    return column.fieldType || "string";
}

/**
 * @param {SdeRow} row
 * @param {SdeColumnDef} column
 * @param {object[]|null|undefined} productTypes
 * @param {Record<string, { label?: string, fieldType?: SdeColumnDef['fieldType'], choices?: string[], unit?: string }>} [fieldInfoMap]
 * @returns {string[]}
 */
export function resolveCellFieldChoices(row, column, productTypes = [], fieldInfoMap = {}) {
    if (column.kind === "metadata" || String(column.key).startsWith("metadata.")) {
        return resolveMetadataFieldDef(row, column.key, productTypes, fieldInfoMap).choices;
    }
    return normalizeFieldChoices(column.choices);
}

/**
 * @param {string} metadataKey
 * @param {Record<string, { label?: string }>} [fieldInfoMap]
 * @returns {string}
 */
export function resolveMetadataColumnLabel(metadataKey, fieldInfoMap = {}) {
    const code = String(metadataKey).replace(/^metadata\./, "");
    return fieldInfoMap[code]?.label || code;
}

/**
 * @param {string} metadataKey
 * @param {Record<string, { label?: string, fieldType?: SdeColumnDef['fieldType'], choices?: string[] }>} [fieldInfoMap]
 * @returns {SdeColumnDef}
 */
export function buildMetadataSdeColumn(metadataKey, fieldInfoMap = {}) {
    const code = String(metadataKey).replace(/^metadata\./, "");
    const info = fieldInfoMap[code] || {};
    return {
        key: metadataKey,
        label: info.label || code,
        kind: "metadata",
        fieldType: info.fieldType || "string",
        choices: normalizeFieldChoices(info.choices),
        unit: info.unit,
        editable: true,
    };
}

/**
 * @param {SdeColumnDef} column
 * @returns {string}
 */
export function formatSdeColumnHeaderLabel(column) {
    if (!column?.label) {
        return column?.key || "";
    }
    if (column.unit) {
        return `${column.label} (${column.unit})`;
    }
    return column.label;
}

/**
 * @param {SdeColumnDef} column
 * @param {Record<string, { unit?: string }>} [fieldInfoMap]
 * @returns {SdeColumnDef}
 */
export function enrichSdeColumnUnit(column, fieldInfoMap = {}) {
    if (!column || column.unit || !String(column.key).startsWith("metadata.")) {
        return column;
    }
    const code = String(column.key).replace(/^metadata\./, "");
    const unit = fieldInfoMap[code]?.unit;
    if (!unit) {
        return column;
    }
    return {
        ...column,
        unit,
    };
}

/**
 * @param {string} metadataKey
 * @returns {import('@/types/structured-data-editor').SdeFieldState}
 */
export function createEmptyMetadataField(metadataKey) {
    return {
        key: metadataKey,
        value: null,
        confidence: { score: null, level: "manual" },
    };
}

/**
 * @param {SdeRow[]} rows
 * @param {string[]} metadataKeys
 * @returns {SdeRow[]}
 */
export function hydrateRowsMetadataFields(rows, metadataKeys = []) {
    if (!metadataKeys?.length) {
        return rows;
    }
    return rows.map((row) => {
        const fields = { ...row.fields };
        let changed = false;
        metadataKeys.forEach((key) => {
            if (!String(key).startsWith("metadata.") || fields[key]) {
                return;
            }
            fields[key] = createEmptyMetadataField(key);
            changed = true;
        });
        return changed ? { ...row, fields } : row;
    });
}

/**
 * @param {SdeRow[]} rows
 * @param {SdeColumnDef[]} columns
 * @param {{
 *   productTypes?: object[],
 *   deletedMetadataKeys?: string[],
 *   extraKeys?: string[],
 * }} [options]
 * @returns {string[]}
 */
export function collectMetadataColumnKeys(rows, columns, options = {}) {
    const { productTypes = [], deletedMetadataKeys = [], extraKeys = [] } = options;
    const existingKeys = new Set(columns.map((col) => col.key));
    const deleted = new Set(deletedMetadataKeys || []);
    const metadataKeys = new Set();

    const maybeAddKey = (key) => {
        if (
            !String(key).startsWith("metadata.") ||
            existingKeys.has(key) ||
            deleted.has(key)
        ) {
            return;
        }
        metadataKeys.add(key);
    };

    rows.forEach((row) => {
        Object.keys(row.fields || {}).forEach((key) => {
            if (key.startsWith("metadata.") && !row.fields[key]?.deleted) {
                maybeAddKey(key);
            }
        });
        const producttype = resolveRowProductType(row, productTypes);
        if (producttype) {
            getProductTypeMetadataFields(producttype).forEach((field) => {
                if (field?.code) {
                    maybeAddKey(`metadata.${field.code}`);
                }
            });
        }
    });

    (extraKeys || []).forEach(maybeAddKey);

    return Array.from(metadataKeys).sort();
}

/**
 * @param {SdeRow} row
 * @param {string} search
 * @param {SdeColumnDef[]} columns
 */
function rowMatchesSearch(row, search, columns) {
    if (!search) {
        return true;
    }
    const needle = search.trim().toLowerCase();
    if (!needle) {
        return true;
    }
    const keys = columns.map((col) => col.key);
    return keys.some((key) => {
        const field = row.fields[key];
        if (!field || field.deleted || field.hidden) {
            return false;
        }
        const value = field.value;
        if (value === null || value === undefined) {
            return false;
        }
        return String(value).toLowerCase().includes(needle);
    });
}

/**
 * True when the row has at least one highlighted sync error (cell or row-level).
 *
 * @param {SdeRow} row
 * @returns {boolean}
 */
export function rowHasSyncError(row) {
    if (row?.syncStatus === "error") {
        return true;
    }
    if (row?.syncRowErrors?.length) {
        return true;
    }
    return Object.values(row?.fields || {}).some((field) => Boolean(field?.syncError));
}

/**
 * @param {SdeRow} row
 * @param {SdeFilterChip} chip
 */
function rowMatchesChip(row, chip) {
    switch (chip) {
        case "all":
            return true;
        case "review":
            return rowHasLowConfidenceField(row);
        case "excluded":
            return row.excluded === true;
        case "modified":
            return rowHasManualField(row);
        case "pending":
            return row.syncStatus === "pending" || row.syncStatus === "error";
        case "errors":
            return rowHasSyncError(row);
        case "manual":
            return row.isManualRow === true;
        default:
            return true;
    }
}

/**
 * @param {SdeRow[]} rows
 * @param {{ search?: string, chip?: SdeFilterChip, columns?: SdeColumnDef[] }} options
 * @returns {SdeRow[]}
 */
export function filterSdeRows(rows, options = {}) {
    const { search = "", chip = "all", columns = [] } = options;
    return rows.filter(
        (row) => rowMatchesSearch(row, search, columns) && rowMatchesChip(row, chip)
    );
}

/**
 * @param {SdeRow} row
 * @param {string} sortBy
 */
function getSdeRowSortValue(row, sortBy) {
    const field = row.fields?.[sortBy];
    if (!field || field.deleted || field.hidden) {
        return null;
    }
    return field.value;
}

/**
 * @param {unknown} left
 * @param {unknown} right
 * @param {string} [fieldType]
 */
function compareSdeSortValues(left, right, fieldType = "string") {
    const leftEmpty = left === null || left === undefined || left === "";
    const rightEmpty = right === null || right === undefined || right === "";
    if (leftEmpty && rightEmpty) {
        return 0;
    }
    if (leftEmpty) {
        return 1;
    }
    if (rightEmpty) {
        return -1;
    }

    if (fieldType === "number" || fieldType === "euros") {
        return Number(left) - Number(right);
    }
    if (fieldType === "boolean") {
        return Number(Boolean(left)) - Number(Boolean(right));
    }
    return String(left).localeCompare(String(right), undefined, {
        sensitivity: "base",
        numeric: true,
    });
}

/**
 * @param {SdeRow[]} rows
 * @param {string|string[]|null|undefined} sortBy
 * @param {boolean|boolean[]} [sortDesc]
 * @param {SdeColumnDef[]} [columns]
 * @returns {SdeRow[]}
 */
export function sortSdeRows(rows, sortBy, sortDesc = false, columns = []) {
    const sortKey = Array.isArray(sortBy) ? sortBy[0] : sortBy;
    const isDesc = Array.isArray(sortDesc) ? Boolean(sortDesc[0]) : Boolean(sortDesc);
    if (!sortKey) {
        return rows;
    }

    const column = columns.find((item) => item.key === sortKey);
    const fieldType = column?.fieldType || "string";

    return [...rows].sort((rowA, rowB) => {
        const comparison = compareSdeSortValues(
            getSdeRowSortValue(rowA, sortKey),
            getSdeRowSortValue(rowB, sortKey),
            fieldType
        );
        return isDesc ? -comparison : comparison;
    });
}

/**
 * @param {SdeRow[]} rows
 * @param {string} [search]
 * @param {SdeColumnDef[]} [columns]
 * @returns {Record<SdeFilterChip, number>}
 */
export function computeSdeChipCounts(rows, search = "", columns = []) {
    const visible = filterSdeRows(rows, { search, chip: "all", columns });
    return {
        all: visible.length,
        review: visible.filter(rowHasLowConfidenceField).length,
        excluded: visible.filter((row) => row.excluded).length,
        modified: visible.filter(rowHasManualField).length,
        pending: visible.filter(
            (row) => row.syncStatus === "pending" || row.syncStatus === "error"
        ).length,
        errors: visible.filter(rowHasSyncError).length,
        manual: visible.filter((row) => row.isManualRow === true).length,
    };
}

/**
 * @param {SdeRow[]} rows
 * @param {SdeRow[]} [filteredRows]
 */
export function computeSdeStats(rows, filteredRows) {
    const visible = filteredRows || rows;
    return {
        total: visible.length,
        lowConfidence: visible.filter(rowHasLowConfidenceField).length,
        excluded: visible.filter((row) => row.excluded).length,
        ready: visible.filter(
            (row) => !row.excluded && row.syncStatus !== "synced"
        ).length,
    };
}

/**
 * @param {SdeRow[]} rows
 * @returns {SdeRow[]}
 */
export function reindexSdeRows(rows) {
    return rows.map((row, index) => ({
        ...row,
        rowIndex: index,
    }));
}

/**
 * @param {SdeColumnDef[]} columns
 * @returns {SdeRow}
 */
export function createEmptySdeRow(columns) {
    /** @type {SdeRow['fields']} */
    const fields = {};
    columns.forEach((column) => {
        fields[column.key] = {
            key: column.key,
            value: column.fieldType === "boolean" ? false : null,
            confidence: { score: null, level: "manual" },
        };
    });
    return {
        rowIndex: 0,
        fields,
        excluded: false,
        syncStatus: "pending",
        isManualRow: true,
    };
}

/**
 * @param {SdeRow} row
 * @returns {SdeRow}
 */
export function duplicateSdeRow(row) {
    const fields = {};
    Object.entries(row.fields || {}).forEach(([key, field]) => {
        fields[key] = {
            ...field,
            confidence: {
                ...field.confidence,
                level: "manual",
            },
        };
    });
    return {
        rowIndex: 0,
        fields,
        excluded: false,
        syncStatus: "pending",
        duplicatedFrom: row.rowIndex,
        isManualRow: true,
    };
}

/**
 * @param {SdeRow[]} rows
 * @returns {number}
 */
export function countImportableRows(rows) {
    return rows.filter((row) => !row.excluded && row.syncStatus !== "synced").length;
}
